import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, getToken } from "../utils/helpers";
import InstanceIcon from "./InstanceIcon";
import "./CustomerDialog.css";
import { HomeAction, HomeButton, HomeInput, HomeCheckbox, HomeDialog } from "./SpacesHomeControls";

const MAX_ASSET_SIZE = 2 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp,image/x-icon,image/vnd.microsoft.icon,.ico";
const sameIds = (a, b) => a.length === b.length && a.every(id => b.includes(id));

// Check the content too: a renamed SVG must never become an uploaded image.
function imageMime(dataUrl) {
  const bytes = atob(dataUrl.split(",")[1] || "");
  if (bytes.startsWith("\x89PNG\r\n\x1a\n")) return "image/png";
  if (bytes.startsWith("\xff\xd8\xff")) return "image/jpeg";
  if (bytes.startsWith("RIFF") && bytes.slice(8, 12) === "WEBP") return "image/webp";
  if (bytes.startsWith("\x00\x00\x01\x00")) return "image/x-icon";
  return null;
}

export function customerErrorReason(error, t) {
  if (error?.code === "INVALID_CUSTOMERS_RESPONSE") return t("customers:errors.invalidResponse");
  if (error?.reason) return error.reason;
  if (error?.status && error?.message && !/^HTTP \d+$/.test(error.message)) return error.message;
  if (error?.status) return t("customers:errors.http", { status: error.status });
  return t("customers:errors.network");
}

export default function CustomerDialog({ customer = null, customers, spaces, spaceInstances = {}, instanceErrors = {}, onClose, onSaved }) {
  const { t } = useTranslation(["customers", "gemeinsam"]);
  const id = useId();
  const [name, setName] = useState(customer?.name || "");
  const [title, setTitle] = useState(customer?.title || "");
  const [spaceIds, setSpaceIds] = useState(customer?.spaceIds || []);
  const [assets, setAssets] = useState({});
  const [fileNames, setFileNames] = useState({});
  const [assetErrors, setAssetErrors] = useState({});
  const [reading, setReading] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [nameError, setNameError] = useState(false);
  const nameInput = useRef(null);
  const fileInputs = useRef({});
  const readers = useRef({});
  const request = useRef(null);
  const mounted = useRef(false);
  const token = useRef(getToken());

  useEffect(() => {
    const activeReaders = readers.current;
    mounted.current = true;
    return () => {
      mounted.current = false;
      request.current?.abort();
      Object.values(activeReaders).forEach(reader => reader.abort());
    };
  }, []);

  const occupied = new Set(customers.filter(item => item.id !== customer?.id).flatMap(item => item.spaceIds));
  const availableSpaces = spaces.filter(space => !space.isShared && !space.mirrorOf && !occupied.has(space.id)
    && (!space.customerId || space.customerId === customer?.id || customer?.spaceIds.includes(space.id)));
  const readingAsset = Object.values(reading).some(Boolean);
  const hasAssetError = Object.values(assetErrors).some(Boolean);
  const current = () => mounted.current && getToken() === token.current;

  const removeAsset = field => {
    readers.current[field]?.abort();
    delete readers.current[field];
    setReading(previous => ({ ...previous, [field]: false }));
    setAssetErrors(previous => ({ ...previous, [field]: null }));
    setAssets(previous => ({ ...previous, [field]: "" }));
    setFileNames(previous => ({ ...previous, [field]: "" }));
  };

  const discardInvalidFile = field => {
    readers.current[field]?.abort();
    delete readers.current[field];
    setReading(previous => ({ ...previous, [field]: false }));
    setAssetErrors(previous => ({ ...previous, [field]: null }));
  };

  const selectFile = (field, event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    readers.current[field]?.abort();
    delete readers.current[field];
    setReading(previous => ({ ...previous, [field]: false }));
    setAssetErrors(previous => ({ ...previous, [field]: null }));
    const allowedType = ["image/png", "image/jpeg", "image/webp", "image/x-icon", "image/vnd.microsoft.icon"].includes(file.type);
    if ((!allowedType && !(file.type === "" && /\.ico$/i.test(file.name))) || /\.svg$/i.test(file.name)) {
      setAssetErrors(previous => ({ ...previous, [field]: "fileType" }));
      return;
    }
    if (file.size > MAX_ASSET_SIZE) {
      setAssetErrors(previous => ({ ...previous, [field]: "fileSize" }));
      return;
    }
    const reader = new FileReader();
    readers.current[field] = reader;
    setReading(previous => ({ ...previous, [field]: true }));
    const active = () => current() && readers.current[field] === reader;
    reader.onload = () => {
      if (!active()) return;
      let mime;
      try { mime = imageMime(String(reader.result)); } catch { /* Invalid image content. */ }
      const expected = file.type === "image/vnd.microsoft.icon" ? "image/x-icon" : file.type;
      if (!mime || (expected && expected !== mime)) {
        setAssetErrors(previous => ({ ...previous, [field]: "fileType" }));
      } else {
        const data = `data:${mime};base64,${String(reader.result).split(",")[1]}`;
        setAssets(previous => ({ ...previous, [field]: data }));
        setFileNames(previous => ({ ...previous, [field]: file.name }));
      }
      setReading(previous => ({ ...previous, [field]: false }));
    };
    reader.onerror = () => {
      if (!active()) return;
      setAssetErrors(previous => ({ ...previous, [field]: "fileRead" }));
      setReading(previous => ({ ...previous, [field]: false }));
    };
    reader.readAsDataURL(file);
  };

  const save = async event => {
    event.preventDefault();
    if (!current() || request.current || readingAsset || hasAssetError) return;
    if (!name.trim()) {
      setNameError(true);
      nameInput.current?.focus();
      return;
    }
    const body = { ...assets };
    if (!customer || name.trim() !== customer.name) body.name = name.trim();
    if (!customer || title.trim() !== (customer.title || "")) body.title = title.trim();
    if (!customer || !sameIds(spaceIds, customer.spaceIds)) body.spaceIds = spaceIds;
    if (!Object.keys(body).length) { onClose(); return; }
    const controller = new AbortController();
    request.current = controller;
    setSaving(true);
    setSaveError(null);
    try {
      await api(customer ? `/api/admin/customers/${encodeURIComponent(customer.id)}` : "/api/admin/customers", {
        method: customer ? "PUT" : "POST", body: JSON.stringify(body), signal: controller.signal,
      });
    } catch (error) {
      if (current() && !controller.signal.aborted) {
        setSaveError(error);
        setSaving(false);
        request.current = null;
      }
      return;
    }
    if (current() && !controller.signal.aborted) onSaved();
  };

  return (
    <HomeDialog onClose={() => { if (!saving) onClose(); }} maxWidth="md"
      aria-labelledby={`${id}-heading`} PaperProps={{ className: "iio customer-dialog-paper" }}>
      <form onSubmit={save} className="customer-dialog-form" noValidate aria-busy={saving}>
        <header className="customer-dialog-head">
          <h2 id={`${id}-heading`}>{t(customer ? "dialog.edit" : "dialog.create")}</h2>
        </header>
        <div className="customer-dialog-body">
          <div className="customer-dialog-identity"><div className="iio-field">
            <label className="iio-field-label" htmlFor={`${id}-name`}>{t("dialog.name")}</label>
            <HomeInput id={`${id}-name`} ref={nameInput} className="iio-input" autoFocus required maxLength={160}
              value={name} disabled={saving} aria-invalid={nameError} aria-describedby={nameError ? `${id}-name-error` : undefined}
              onChange={event => { setName(event.target.value); setNameError(false); }}/>
            {nameError && <p id={`${id}-name-error`} role="alert" className="customer-dialog-error">{t("errors.nameRequired")}</p>}
          </div>
          <div className="iio-field">
            <label className="iio-field-label" htmlFor={`${id}-title`}>{t("dialog.title")}</label>
            <HomeInput id={`${id}-title`} className="iio-input" value={title} maxLength={160} disabled={saving}
              aria-describedby={`${id}-title-hint`} onChange={event => setTitle(event.target.value)}/>
            <p id={`${id}-title-hint`} className="customer-dialog-hint">{t("dialog.titleHint")}</p>
          </div></div>
          <div className="customer-dialog-assets">
            {["logoBase64", "faviconBase64"].map(field => {
              const kind = field === "logoBase64" ? "logo" : "favicon";
              const preview = Object.hasOwn(assets, field) ? assets[field] : customer?.[`${kind}Url`];
              return <div key={field} className="iio-field customer-dialog-asset">
                <label className="iio-field-label" htmlFor={`${id}-${kind}`}>{t(`dialog.${kind}`)}</label>
                <input id={`${id}-${kind}`} ref={element => { fileInputs.current[field] = element; }}
                  className="customer-dialog-file-input" tabIndex={-1} type="file" accept={ACCEPT} disabled={saving}
                  aria-describedby={`${id}-${kind}-hint${assetErrors[field] ? ` ${id}-${kind}-error` : ""}`}
                  aria-invalid={!!assetErrors[field]} onChange={event => selectFile(field, event)}/>
                <HomeAction type="button" className={`customer-dialog-upload customer-dialog-upload--${kind}`}
                  disabled={saving || reading[field]} onClick={() => fileInputs.current[field]?.click()}
                  aria-label={t(reading[field] ? "dialog.readingImage" : `dialog.${kind}${preview ? "Change" : "Choose"}`)}
                  aria-describedby={`${id}-${kind}-hint${assetErrors[field] ? ` ${id}-${kind}-error` : ""}`}>
                  <span className="customer-dialog-preview">
                    {preview ? <img src={preview} alt={t(`dialog.${kind}Preview`)}/> : <svg aria-hidden="true" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      {kind === "logo" ? <><rect x="3" y="3" width="18" height="18" rx="3"/><circle cx="8" cy="8" r="1.5"/><path d="m3 17 5-5 4 4 3-3 6 6"/></> : <><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M3 8h18M8 3v5"/><path d="M12 11v6m-3-3h6"/></>}
                    </svg>}
                  </span>
                  <span className="customer-dialog-upload-action">{t(reading[field] ? "dialog.readingImage" : `dialog.${kind}${preview ? "Change" : "Choose"}`)}</span>
                  <span className="customer-dialog-filename">{fileNames[field] || t(preview ? "dialog.savedImage" : `dialog.${kind}Hint`)}</span>
                </HomeAction>
                <p id={`${id}-${kind}-hint`} className="customer-dialog-hint">{t("dialog.fileHint")}</p>
                {reading[field] && <p role="status" className="customer-dialog-hint">{t("gemeinsam:zustand.laedt")}</p>}
                {assetErrors[field] && <p id={`${id}-${kind}-error`} className="customer-dialog-error" role="alert">{t(`errors.${assetErrors[field]}`)}</p>}
                {(preview || reading[field] || assetErrors[field]) && <HomeButton type="button" className="iio-btn iio-btn--secondary"
                  disabled={saving} onClick={() => assetErrors[field] ? discardInvalidFile(field) : removeAsset(field)}>
                  {t(`dialog.${kind}${assetErrors[field] ? "Discard" : "Remove"}`)}</HomeButton>}
              </div>;
            })}
          </div>
          <fieldset className="customer-dialog-spaces" disabled={saving} aria-describedby={`${id}-spaces-hint`}>
            <legend>{t("dialog.spaces")}</legend>
            <p id={`${id}-spaces-hint`} className="customer-dialog-hint">{t("dialog.spacesHint")}</p>
            {availableSpaces.length === 0 && <p className="customer-dialog-hint">{t("dialog.noSpaces")}</p>}
            <div className="customer-dialog-space-grid">{availableSpaces.map(space => <label key={space.id} className={`customer-dialog-space${spaceIds.includes(space.id) ? " is-selected" : ""}`}>
              <HomeCheckbox disabled={saving} aria-label={space.name} checked={spaceIds.includes(space.id)} onChange={event => setSpaceIds(previous => event.target.checked
                ? [...previous, space.id] : previous.filter(value => value !== space.id))}/>
              <span className="customer-dialog-space-content">
                <span className="customer-dialog-space-heading"><span className="customer-dialog-space-icon"><InstanceIcon name={space.icon}/></span><strong>{space.name}</strong></span>
                <span className="customer-dialog-instance-list">
                  {instanceErrors[space.id] ? <span className="customer-dialog-hint">{t("dialog.instancesError")}</span>
                    : !Object.hasOwn(spaceInstances, space.id) ? <span className="customer-dialog-hint" role="status">{t("dialog.instancesLoading")}</span>
                    : spaceInstances[space.id].length === 0 ? <span className="customer-dialog-hint">{t("dialog.noInstances")}</span>
                    : spaceInstances[space.id].map(instance => <span key={instance.id} className="customer-dialog-instance-name">{instance.name}</span>)}
                </span>
              </span>
              <span className="customer-dialog-space-selected" aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 4 4L19 6"/></svg>
                <strong>{space.name}</strong>
                <span>{t("dialog.selected")}</span>
              </span>
            </label>)}</div>
          </fieldset>
          {saveError && <p className="customer-dialog-error" role="alert">{t("errors.save", { reason: customerErrorReason(saveError, t) })}</p>}
        </div>
        <footer className="customer-dialog-foot">
          <HomeButton type="button" className="iio-btn iio-btn--secondary" onClick={onClose} disabled={saving}>{t("gemeinsam:aktion.abbrechen")}</HomeButton>
          <HomeButton type="submit" className="iio-btn iio-btn--primary" disabled={saving || readingAsset || hasAssetError}>
            {saving ? t("dialog.saving") : t("gemeinsam:aktion.speichern")}
          </HomeButton>
        </footer>
      </form>
    </HomeDialog>
  );
}
